import { RouterProvider } from '@tanstack/react-router'
import { ZeroProvider } from '@rocicorp/zero/react'
import { useCallback, useRef } from 'react'
import { AuthProvider, useAuth } from './auth/AuthContext.tsx'
import { router } from './router.tsx'
import { mutators } from './zero/mutators.ts'
import { schema } from './zero/schema.ts'

interface ZeroHandle {
  readonly userID: string | undefined
  delete(): Promise<unknown>
}

/**
 * Builds the Zero client from the current session. The last client is retained
 * across logout so its local store can be reused if the same user signs back in.
 * When a different user signs in, that prior user's store is dropped.
 *
 * `server` is the browser-facing zero-cache URL; the websocket sync connects
 * straight to it, while /api/* calls go through Vite's dev proxy to Hono.
  */
function ZeroRoot() {
  const { status, session } = useAuth()
  const previousZero = useRef<{ userID: string; zero: ZeroHandle } | null>(null)
  const handleZeroInit = useCallback((zero: ZeroHandle) => {
    const userID = zero.userID
    if (!userID) return

    const previous = previousZero.current
    if (previous && previous.userID !== userID) {
      void previous.zero.delete().catch(() => {
        // Never block the new user's session on local cleanup.
      })
    }
    previousZero.current = { userID, zero }
  }, [])

  if (status === 'loading') {
    return (
      <div className="app-layout">
        <div className="pinpad-screen">
          <p className="muted">Restoring session…</p>
        </div>
      </div>
    )
  }
  if (!session) return <RouterProvider router={router} />

  return (
    <ZeroProvider
      key={session.sessionId}
      schema={schema}
      server={import.meta.env.VITE_PUBLIC_SERVER}
      userID={session.auth.sub}
      auth={session.token}
      mutators={mutators}
      context={session.auth}
      init={handleZeroInit}
    >
      <RouterProvider router={router} />
    </ZeroProvider>
  )
}

export function App() {
  return (
    <AuthProvider>
      <ZeroRoot />
    </AuthProvider>
  )
}
